import React from "react";
import { Box } from "@mui/material";
import { NavLink } from "react-router-dom";
import useReveal from "./useReveal";
import PageHero from "./PageHero";
import {
  PlanningArt,
  BurndownArt,
  CollaborationArt,
  HeroBoardArt,
  IconBoard,
  IconBacklog,
  IconChart,
  IconUsers,
  IconShield,
  IconBell,
  IconCheck,
  IconArrow,
} from "../Home/landingArt";
import {
  IconSprint,
  IconTarget,
  IconFilter,
  IconHistory,
  IconTag,
  IconLink,
  IconSearch,
  IconLayers,
  IconCompass,
  IconLifebuoy,
} from "./marketingArt";
import "../Home/landingpage.scss";
import "./marketing.scss";

/* The four things the product does, each shown with its own art. */
const PILLARS = [
  {
    eyebrow: "Plan",
    title: "Turn a backlog into a plan the team believes",
    lead:
      "Size the work before you commit to it. Estimate in story points, set priority, group work into epics, and pull a sprint together when the team is ready — not because the calendar said so.",
    art: <PlanningArt />,
    points: [
      "Story point estimates and priority on every issue",
      "Epics to group work that spans several sprints",
      "Drag issues from backlog into the sprint you are shaping",
      "Start and complete sprints with a scope everyone can see",
    ],
  },
  {
    eyebrow: "Track",
    title: "See where the sprint really stands",
    lead:
      "The board shows what is in flight; the burn-down shows whether you will finish. Remaining work is compared against the ideal line and updates as issues move, so slippage surfaces days before the sprint review.",
    art: <BurndownArt />,
    flipped: true,
    points: [
      "Burn-down with an ideal-versus-actual guide",
      "Issue breakdown by status, assignee and type",
      "Sprint-over-sprint history to spot a pattern",
      "Progress that updates as the board changes — no manual status report",
    ],
  },
  {
    eyebrow: "Collaborate",
    title: "Keep the conversation on the work",
    lead:
      "The reason a decision was made belongs next to the thing it changed. Comments, replies and a full field-by-field history live on the issue, so nobody has to reconstruct a thread from chat six weeks later.",
    art: <CollaborationArt />,
    points: [
      "Threaded comments and replies on every issue",
      "Mentions that notify the right person",
      "A complete change history, field by field",
      "Rich text descriptions, checklists and attachments",
    ],
  },
  {
    eyebrow: "Deliver",
    title: "One board the whole team actually uses",
    lead:
      "Columns match your workflow, not someone else's idea of it. Story points, priority, assignee and issue type stay visible on the card, so a stand-up can be run straight from the board.",
    art: <HeroBoardArt />,
    flipped: true,
    points: [
      "Workflow columns you define per project",
      "Drag and drop across statuses",
      "Assignee, priority and points visible at a glance",
      "Filter the board down to one person, epic or issue type",
    ],
  },
];

/* The full capability list, for the reader who wants to check a specific box. */
const CAPABILITIES = [
  {
    icon: <IconBoard />,
    tone: "is-green",
    title: "Sprint board",
    body: "A drag-and-drop board on your own workflow columns, with everything the card needs to be read at a glance.",
  },
  {
    icon: <IconBacklog />,
    tone: "is-blue",
    title: "Backlog & grooming",
    body: "Keep upcoming work ordered and sized so sprint planning is a decision, not a scramble.",
  },
  {
    icon: <IconSprint />,
    tone: "is-violet",
    title: "Sprint lifecycle",
    body: "Start, run and complete sprints with a defined scope and end date. Unfinished work rolls forward cleanly.",
  },
  {
    icon: <IconChart />,
    tone: "is-amber",
    title: "Burn-down & reports",
    body: "Remaining work against the ideal line, plus breakdowns by status and assignee, sprint after sprint.",
  },
  {
    icon: <IconLayers />,
    tone: "is-blue",
    title: "Epics & hierarchy",
    body: "Group work that spans sprints under an epic and follow it through to completion.",
  },
  {
    icon: <IconTarget />,
    tone: "is-green",
    title: "Estimation & priority",
    body: "Story points and priority on every issue, so capacity conversations are grounded in something.",
  },
  {
    icon: <IconUsers />,
    tone: "is-violet",
    title: "Team collaboration",
    body: "Threaded comments, replies and mentions that keep the decision beside the work it changed.",
  },
  {
    icon: <IconHistory />,
    tone: "is-amber",
    title: "Change history",
    body: "Every field change recorded — who changed what, and when — for any issue in the project.",
  },
  {
    icon: <IconSearch />,
    tone: "is-green",
    title: "Search & filters",
    body: "Find an issue by title, assignee, status, type or epic without leaving what you were doing.",
  },
  {
    icon: <IconShield />,
    tone: "is-blue",
    title: "Roles & permissions",
    body: "Project managers, leads, scrum masters and developers each get exactly the access they need.",
  },
  {
    icon: <IconBell />,
    tone: "is-violet",
    title: "Notifications",
    body: "Assignments, status changes and replies reach the right people in the app and by email.",
  },
  {
    icon: <IconTag />,
    tone: "is-amber",
    title: "Issue types & workflow",
    body: "Stories, tasks, bugs and sub-tasks, each moving through the statuses your team agreed on.",
  },
];

/* Same product, read four different ways — the shape a buying committee has. */
const ROLES = [
  {
    icon: <IconCompass />,
    tone: "is-green",
    title: "For the project manager",
    body: "Scope, progress and risk in one view. Know what is committed, what has moved and what will not land — before the review, not during it.",
  },
  {
    icon: <IconSprint />,
    tone: "is-blue",
    title: "For the scrum master",
    body: "Run planning, stand-up and retro from the same workspace. The board is the agenda; the burn-down is the evidence.",
  },
  {
    icon: <IconBoard />,
    tone: "is-violet",
    title: "For the developer",
    body: "A clear queue of work with the context attached. Pick up an issue, see the history, leave the reasoning behind for whoever is next.",
  },
  {
    icon: <IconChart />,
    tone: "is-amber",
    title: "For leadership",
    body: "Delivery trends across projects and sprints, drawn from what teams actually did rather than a status slide assembled the night before.",
  },
];

const DIFFERENTIATORS = [
  {
    icon: <IconTarget />,
    title: "Built for scrum, not bent into it",
    body: "Sprints, story points, epics and burn-downs are first-class here — not a generic task list with agile vocabulary layered on top.",
  },
  {
    icon: <IconLink />,
    title: "One workspace, not five tabs",
    body: "Board, backlog, issues, reports and the discussion around them all live together, so context does not leak into chat and spreadsheets.",
  },
  {
    icon: <IconFilter />,
    title: "Quick to set up, quick to learn",
    body: "Create a project, invite the team, define your columns and start a sprint. Nothing about the first week needs a consultant.",
  },
  {
    icon: <IconLifebuoy />,
    title: "Support that knows the product",
    body: "Questions go to people who work on Projam, not a script. Every plan includes support; higher plans get a priority queue.",
  },
];

const Features = () => {
  const rootRef = useReveal();

  return (
    <Box className="pj-landing pj-page" ref={rootRef}>
      <PageHero
        eyebrow="Features"
        title={
          <>
            Everything an agile team needs,{" "}
            <span className="lp-gradient-text">in one workspace</span>
          </>
        }
        lead="Plan the sprint, run the board, watch the burn-down and keep the discussion beside the work. Projam covers the whole loop, so your team stops stitching tools together and gets on with shipping."
      >
        <NavLink to="/register" className="lp-btn lp-btn--primary">
          Get started free <IconArrow />
        </NavLink>
        <NavLink to="/contact" className="lp-btn lp-btn--ghost">
          Talk to sales
        </NavLink>
      </PageHero>

      {/* ------------------------------------------------------- the pillars */}
      <section className="lp-section">
        <div className="lp-wrap">
          {PILLARS.map((pillar, index) => (
            <div
              className={`lp-showcase ${pillar.flipped ? "is-flipped" : ""}`}
              key={pillar.title}
              style={index === 0 ? { marginTop: 0 } : undefined}
            >
              <div>
                <span className="lp-eyebrow" data-reveal>
                  {pillar.eyebrow}
                </span>
                <h2 className="lp-h2" data-reveal data-reveal-delay="70">
                  {pillar.title}
                </h2>
                <p className="lp-lead" data-reveal data-reveal-delay="120">
                  {pillar.lead}
                </p>
                <ul className="lp-checks" data-reveal data-reveal-delay="180">
                  {pillar.points.map((point) => (
                    <li key={point}>
                      <span className="lp-check">
                        <IconCheck />
                      </span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
              <div
                className="lp-showcase-art"
                data-reveal
                data-reveal-delay="120"
              >
                {pillar.art}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* -------------------------------------------------- full capability */}
      <section className="lp-section pg-band">
        <div className="lp-wrap">
          <div className="lp-head">
            <span className="lp-eyebrow" data-reveal>
              The complete list
            </span>
            <h2 className="lp-h2" data-reveal data-reveal-delay="80">
              Every capability, on every plan
            </h2>
            <p className="lp-lead" data-reveal data-reveal-delay="140">
              The core of Projam is not held back for a higher tier. Plans
              differ by how many people they cover and how much administration
              a larger organisation needs.
            </p>
          </div>

          <div className="lp-grid">
            {CAPABILITIES.map((item, i) => (
              <article
                className="lp-card"
                key={item.title}
                data-reveal
                data-reveal-delay={(i % 3) * 70}
              >
                <div className={`lp-card-icon ${item.tone}`}>{item.icon}</div>
                <h3 className="lp-h3">{item.title}</h3>
                <p className="lp-body">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- for roles */}
      <section className="lp-section">
        <div className="lp-wrap">
          <div className="lp-head">
            <span className="lp-eyebrow" data-reveal>
              Made for the whole team
            </span>
            <h2 className="lp-h2" data-reveal data-reveal-delay="80">
              One tool, four points of view
            </h2>
            <p className="lp-lead" data-reveal data-reveal-delay="140">
              Everyone in delivery needs something different from the same set
              of facts. Projam gives each role their own way in.
            </p>
          </div>

          <div className="pg-roles">
            {ROLES.map((role, i) => (
              <article
                className="pg-role"
                key={role.title}
                data-reveal
                data-reveal-delay={(i % 2) * 90}
              >
                <div className={`lp-card-icon ${role.tone}`}>{role.icon}</div>
                <div>
                  <h3 className="lp-h3">{role.title}</h3>
                  <p className="lp-body">{role.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------- why teams pick us */}
      <section className="lp-section pg-band">
        <div className="lp-wrap">
          <div className="lp-head">
            <span className="lp-eyebrow" data-reveal>
              Why Projam
            </span>
            <h2 className="lp-h2" data-reveal data-reveal-delay="80">
              What makes it different
            </h2>
          </div>

          <div className="lp-grid pg-grid-4">
            {DIFFERENTIATORS.map((item, i) => (
              <article
                className="lp-card"
                key={item.title}
                data-reveal
                data-reveal-delay={i * 70}
              >
                <div className="lp-card-icon is-green">{item.icon}</div>
                <h3 className="lp-h3">{item.title}</h3>
                <p className="lp-body">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------- cta */}
      <section className="lp-section">
        <div className="lp-wrap">
          <div className="lp-cta" data-reveal>
            <h2 className="lp-h2">See it on your own sprint</h2>
            <p>
              Create a project, invite your team and plan your first sprint in
              minutes — or let us walk you through it.
            </p>
            <div className="lp-cta-actions">
              <NavLink to="/register" className="lp-btn lp-btn--white">
                Get started free <IconArrow />
              </NavLink>
              <NavLink to="/pricing" className="lp-btn lp-btn--light">
                See pricing
              </NavLink>
            </div>
          </div>
        </div>
      </section>
    </Box>
  );
};

export default Features;
